import { useCallback, useEffect, useRef, useState } from "react";

import { usePageLayoutContext } from "contexts/PageLayoutContext";

import { standardWaitTime, stateUpdateWaitTime } from "constants/time";

const updatePositionOnScreenResize = (
  updatePosition: () => void,
  elementToScroll?: HTMLDivElement | HTMLElement
) => {
  if (typeof window !== "undefined") {
    updatePosition();
    if (elementToScroll)
      elementToScroll.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    const divElements = document.querySelectorAll("div");

    // Iterate over each div element and attach a scroll event listener
    divElements.forEach((div) => {
      div.addEventListener("scroll", updatePosition);
    });

    return () => {
      window.removeEventListener("resize", updatePosition);
      if (elementToScroll)
        elementToScroll.removeEventListener("scroll", updatePosition, true);
    };
  }
};

type Props = {
  parentDiv?: HTMLDivElement | HTMLElement | null;
  callBack?: () => void;
};

export const useElementPostion = ({ parentDiv, callBack }: Props) => {
  const parentRef = useRef<HTMLDivElement | HTMLButtonElement | null>(null);
  const { leftPanelStatus } = usePageLayoutContext();
  const callBackRef = useRef<(() => void) | undefined>(callBack);
  const [left, setLeft] = useState<number>(0);
  const [bottom, setBottom] = useState<number>(0);
  const [right, setRight] = useState<number>(0);
  const [top, setTop] = useState<number>(0);

  useEffect(() => {
    callBackRef.current = callBack;
  }, [callBack]);

  const updatePosition = useCallback(() => {
    const currentCallBack = callBackRef.current; // Get the latest callBack from the ref
    if (currentCallBack) currentCallBack(); // Call callBack if it exists
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setLeft(rect.left);
      setBottom(rect.bottom);
      setRight(rect.right);
      setTop(rect.top);
    }
  }, []);

  // when page is first loaded, wait for all components to render before setting the positions.
  useEffect(() => {
    setTimeout(() => {
      updatePosition();
    }, standardWaitTime);
  }, [parentRef, updatePosition]);

  useEffect(() => {
    setTimeout(() => {
      updatePosition();
    }, stateUpdateWaitTime);
  }, [leftPanelStatus, updatePosition]);

  useEffect(() => {
    updatePositionOnScreenResize(
      updatePosition,
      parentDiv ? parentDiv : undefined
    );
  }, [updatePosition, parentDiv]);

  return { left, bottom, right, top, ref: parentRef };
};
